'use client'

import type * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { UFile, bytes } from '@w/utils'
import { useAudioRecorder } from './use-audio-recorder'
import { useTranslation } from '../i18n'

import type { FileType, RejectReason, FileInputOptions, FileInputState, FileInputActions } from './use-file-input.types'
export type { FileInputOptions, FileInputState, FileInputActions } from './use-file-input.types'

const acceptMap: Record<FileType, string | undefined> = {
  file: undefined,
  image: 'image/*',
  audio: 'audio/*',
  video: 'video/*',
}

const youtubeIdRegex =
  /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/u

export function useFileInput(
  options: FileInputOptions = {},
): [FileInputState, FileInputActions] {
  const {
    fileType = 'file',
    accept,
    multiple = false,
    maxSize,
    maxFiles,
    initialFiles = [],
    onFilesChange,
    onFilesAdded,
    youtubeId = [],
    onYoutubeId,
  } = options

  const { t } = useTranslation('ui')
  const containerRef = useRef<HTMLSpanElement>(null)
  const [files, setFiles] = useState(initialFiles)
  const [isDragOver, setIsDragOver] = useState(false)
  const [dragRejectReason, setDragRejectReason] = useState<RejectReason | null>(
    null,
  )
  const [flashRejectReason, setFlashRejectReason] =
    useState<RejectReason | null>(null)
  const pasteRejectTimer = useRef<ReturnType<typeof setTimeout>>(null)
  const dragCounter = useRef(0)

  const resolvedAccept = accept ?? acceptMap[fileType]

  const flashReject = (reason: RejectReason) => {
    if (pasteRejectTimer.current) clearTimeout(pasteRejectTimer.current)
    setFlashRejectReason(reason)
    pasteRejectTimer.current = setTimeout(() => {
      setFlashRejectReason(null)
    }, 2000)
  }

  useEffect(
    () => () => {
      if (pasteRejectTimer.current) clearTimeout(pasteRejectTimer.current)
    },
    [],
  )

  // Keep refs for callbacks to avoid stale closures
  const onFilesChangeRef = useRef(onFilesChange)
  const onFilesAddedRef = useRef(onFilesAdded)
  useEffect(() => {
    onFilesChangeRef.current = onFilesChange
    onFilesAddedRef.current = onFilesAdded
  }, [onFilesChange, onFilesAdded])

  const notifyChange = (next: UFile[]) => {
    onFilesChangeRef.current?.(next)
  }

  const addFiles = (incoming: File[]): boolean => {
    const ufiles = incoming.map((f) => new UFile(f))
    // Filter by accept type
    const accepted = ufiles.filter((f) => fileMatchesAccept(f, resolvedAccept))
    if (accepted.length === 0) {
      if (ufiles.length > 0) flashReject('type')
      return false
    }
    // Filter by maxSize
    // oxlint-disable-next-line eslint/eqeqeq -- intentional loose check: null or undefined both signal "no value"
    if (maxSize != null) {
      const tooLarge = accepted.some((f) => f.size > maxSize)
      if (tooLarge) {
        flashReject('size')
        const valid = accepted.filter((f) => f.size <= maxSize)
        if (valid.length === 0) return false
        // Re-convert valid ones back to File for recursion
        return addFiles(
          valid.map((u) => new File([u.blob], u.name, { type: u.type })),
        )
      }
    }
    // Check maxFiles
    // oxlint-disable-next-line eslint/eqeqeq -- intentional loose check: null or undefined both signal "no value"
    if (maxFiles != null && multiple) {
      const currentCount = files.length
      if (currentCount + accepted.length > maxFiles) {
        flashReject('maxFiles')
        return false
      }
    }
    setFiles((prev) => {
      const next = multiple ? dedupFiles(prev, accepted) : accepted.slice(0, 1)
      // Re-check maxFiles after dedup
      // oxlint-disable-next-line eslint/eqeqeq -- intentional loose check: null or undefined both signal "no value"
      if (maxFiles != null && multiple && next.length > maxFiles) {
        queueMicrotask(() => {
          flashReject('maxFiles')
        })
        return prev
      }
      queueMicrotask(() => {
        syncInputFiles(containerRef.current, next, false)
        notifyChange(next)
      })
      onFilesAddedRef.current?.(accepted)
      return next
    })
    return true
  }

  const removeFile = (id: string) => {
    setFiles((prev) => {
      const next = prev.filter((f) => f.id !== id)
      queueMicrotask(() => {
        syncInputFiles(containerRef.current, next, false)
        notifyChange(next)
      })
      return next
    })
  }

  const clearFiles = () => {
    setFiles([])
    syncInputFiles(containerRef.current, [], false)
    notifyChange([])
  }

  const removeYoutubeId = (id: string) => {
    onYoutubeId?.(youtubeId.filter((y) => y !== id))
  }

  const clearYoutubeIds = () => {
    onYoutubeId?.([])
  }

  const recorder = useAudioRecorder({
    onFile: (file) => {
      addFiles([file])
    },
  })

  const handleClipboardPaste = async () => {
    try {
      const items = await navigator.clipboard.read()
      for (const item of items) {
        const imageType = item.types.find((t_) => t_.startsWith('image/'))
        if (imageType) {
          const blob = await item.getType(imageType)
          const ext = imageType.split('/')[1] ?? 'png'
          const file = new File([blob], `pasted.${ext}`, { type: imageType })
          if (addFiles([file])) return
        }
      }
      flashReject('type')
    } catch {
      try {
        const text = await navigator.clipboard.readText()
        if (text.startsWith('http')) {
          const res = await fetch(text)
          const blob = await res.blob()
          const ext = blob.type.split('/')[1] ?? 'bin'
          const file = new File([blob], `pasted.${ext}`, { type: blob.type })
          if (addFiles([file])) return
        }
        flashReject('type')
      } catch {
        flashReject('type')
      }
    }
  }

  const handleYoutubePaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      const match = text.match(youtubeIdRegex)
      if (match?.[2] && match[2].length === 11) {
        const id = match[2]
        if (!youtubeId.includes(id)) onYoutubeId?.([...youtubeId, id])
        return
      }
      flashReject('youtube')
    } catch {
      flashReject('youtube')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputFiles = Array.from(e.target.files ?? [])
    if (inputFiles.length === 0) return
    if (multiple) {
      const ufiles = inputFiles.map((f) => new UFile(f))
      const next = dedupFiles(files, ufiles)
      // oxlint-disable-next-line eslint/eqeqeq -- intentional loose check: null or undefined both signal "no value"
      if (maxFiles != null && next.length > maxFiles) {
        flashReject('maxFiles')
      } else {
        setFiles(next)
        queueMicrotask(() => {
          notifyChange(next)
        })
      }
    } else {
      const next = [new UFile(inputFiles[0])]
      setFiles(next)
      queueMicrotask(() => {
        notifyChange(next)
      })
    }
  }

  const openFileDialog = () => {
    const input = containerRef.current?.querySelector<HTMLInputElement>(
      '[data-slot="input"]',
    )
    input?.click()
  }

  const hasFiles = files.length > 0
  const hasYoutube = fileType === 'video' && youtubeId.length > 0 && !multiple
  const hasContent = hasFiles || hasYoutube

  const rejectReason = flashRejectReason ?? dragRejectReason
  const isRejected = !!rejectReason
  const rejectMessage = (() => {
    // oxlint-disable-next-line typescript/switch-exhaustiveness-check -- null and 'type' intentionally fall through to default (wrongType message)
    switch (rejectReason) {
      case 'count':
        return t('fileInput.uploadOnlyOne')
      case 'size':
        return t('fileInput.tooLarge', {
          size: bytes.format(maxSize ?? 0, { decimals: 0, capitalize: true }),
        })
      case 'maxFiles':
        return t('fileInput.maxFiles', { count: String(maxFiles ?? 0) })
      case 'youtube':
        return t('fileInput.noYoutubeLink')
      default:
        return t('fileInput.wrongType')
    }
  })()

  const dragHandlers = {
    onDragEnter:
      !hasContent || multiple
        ? (e: React.DragEvent) => {
            dragCounter.current++
            const items = e.dataTransfer.items
            let fileCount = 0
            for (let i = 0; i < items.length; i++) {
              if (items[i].kind === 'file') fileCount++
            }
            const tooMany = !multiple && fileCount > 1
            const exceedsMax =
              // oxlint-disable-next-line eslint/eqeqeq -- intentional loose check: null or undefined both signal "no value"
              maxFiles != null &&
              multiple &&
              files.length + fileCount > maxFiles
            const wrongType = !dragItemsMatchAccept(items, resolvedAccept)
            setIsDragOver(true)
            setDragRejectReason(
              tooMany
                ? 'count'
                : exceedsMax
                  ? 'maxFiles'
                  : wrongType
                    ? 'type'
                    : null,
            )
          }
        : (_e: React.DragEvent) => {},
    onDragOver: (_e: React.DragEvent) => {
      _e.preventDefault()
    },
    onDragLeave:
      !hasContent || multiple
        ? (_e: React.DragEvent) => {
            if (--dragCounter.current === 0) {
              setIsDragOver(false)
              setDragRejectReason(null)
            }
          }
        : (_e: React.DragEvent) => {},
    onDrop:
      !hasContent || multiple
        ? (e: React.DragEvent) => {
            e.preventDefault()
            dragCounter.current = 0
            setIsDragOver(false)
            setDragRejectReason(null)
            const droppedFiles = Array.from(e.dataTransfer.files)
            if (!multiple && droppedFiles.length > 1) {
              flashReject('count')
              return
            }
            if (
              // oxlint-disable-next-line eslint/eqeqeq -- intentional loose check: null or undefined both signal "no value"
              maxFiles != null &&
              multiple &&
              files.length + droppedFiles.length > maxFiles
            ) {
              flashReject('maxFiles')
              return
            }
            if (droppedFiles.length > 0) addFiles(droppedFiles)
          }
        : (e: React.DragEvent) => {
            e.preventDefault()
          },
  }

  const state: FileInputState = {
    files,
    isDragOver,
    rejectReason,
    isRejected,
    rejectMessage,
    recorder,
    resolvedAccept,
    fileType,
    multiple,
    hasFiles,
    hasYoutube,
    hasContent,
    youtubeId,
    maxSize,
    maxFiles,
  }

  const actions: FileInputActions = {
    addFiles,
    removeFile,
    clearFiles,
    openFileDialog,
    handleClipboardPaste,
    handleYoutubePaste,
    removeYoutubeId,
    clearYoutubeIds,
    dragHandlers,
    handleInputChange,
    containerRef,
  }

  return [state, actions]
}

// --- Utilities ---

function mimeMatchesAccept(mime: string, accept: string | undefined): boolean {
  if (!accept) return true
  const patterns = accept.split(',').map((s) => s.trim())
  return patterns.some((pattern) => {
    if (pattern.endsWith('/*')) {
      return mime.startsWith(pattern.slice(0, -1))
    }
    return mime === pattern
  })
}

function fileMatchesAccept(
  file: UFile | File,
  accept: string | undefined,
): boolean {
  if (!accept) return true
  if (file.type && mimeMatchesAccept(file.type, accept)) return true
  const patterns = accept.split(',').map((s) => s.trim())
  const ext = `.${file.name.split('.').pop()?.toLowerCase()}`
  return patterns.some((p) => p.startsWith('.') && p.toLowerCase() === ext)
}

function dragItemsMatchAccept(
  items: DataTransferItemList,
  accept: string | undefined,
): boolean {
  if (!accept) return true
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    if (item.kind !== 'file') continue
    if (!(item.type && mimeMatchesAccept(item.type, accept))) return false
  }
  return true
}

function dedupFiles(existing: UFile[], incoming: UFile[]): UFile[] {
  const result = [...existing]
  for (const f of incoming) {
    if (!result.some((e) => e.name === f.name && e.size === f.size)) {
      result.push(f)
    }
  }
  return result
}

function buildDataTransfer(files: UFile[]): DataTransfer {
  const dt = new DataTransfer()
  for (const f of files) {
    dt.items.add(new File([f.blob], f.name, { type: f.type }))
  }
  return dt
}

function syncInputFiles(
  container: HTMLSpanElement | null,
  files: UFile[],
  dispatchChange = true,
) {
  const input = container?.querySelector<HTMLInputElement>(
    '[data-slot="input"]',
  )
  if (!input) return
  input.files = buildDataTransfer(files).files
  if (dispatchChange)
    input.dispatchEvent(new Event('change', { bubbles: true }))
}

export type { FileType, RejectReason }
